import '../js/open-source/zipjs/zip.js';
import { getFile } from '../elements/katapult-maps-mobile/OPFSStore.js';
import { getQueueRecordsForJob } from '../elements/katapult-maps-mobile/PhotoUpload/UploadQueueStore.js';
import { removePhotoFromQueue } from '../elements/katapult-maps-mobile/PhotoUpload/PhotoUploadService.js';
/** @import { QueueRecord } from '../elements/katapult-maps-mobile/PhotoUpload/UploadQueueStore.js' */

/**
 * Zip the job data and give it to the user in the proper format
 * @param {string} jobId - The job id to zip
 * @returns {Promise<Blob>} - The zip file as a blob
 */
export async function zipPendingJobData(jobId) {
  // Get all photos in the queue for the job
  const queuedPhotos = await getPhotosInUploadQueueForJob(jobId);

  // Get the unsaved changes for the job
  const unsavedJobChanges = await globalThis.FirebaseWorker.getUnsavedChangesForJob(jobId);

  // Add all the data to the zip writer
  const zipWriter = await setUpZipWriter();
  await addPhotosToZip(queuedPhotos, zipWriter);
  await addUnsavedChangesToZip(unsavedJobChanges, zipWriter);

  // Finalize the zip and return it
  const zipAsBlob = await getZipAsBlob(zipWriter);
  return zipAsBlob;
}

/**
 * Clears all the pending data for a job, including queued photos and pending/outgoing changes.
 * @param {string} jobId - The ID of the job for which to clear pending data.
 */
export async function clearPendingJobData(jobId) {
  // Clear all queued photos for the job
  await clearQueuedPhotosForJob(jobId);

  // Clear all unsaved changes for the job
  await globalThis.FirebaseWorker.clearUnsavedChangesForJob(jobId);
}

/**
 * Sets up a new blob zip writer
 * @returns {Promise<object>} - The zip writer object
 */
async function setUpZipWriter() {
  const zipWriter = await new Promise((resolve, reject) => {
    // Set up the zip writer so that blobs can be written to it
    const blobWriter = new zip.BlobWriter();
    // Resolve the promise when the writer is set up
    const writerIsSetUpCallback = (writer) => resolve(writer);
    // Reject the promise if there is an error setting up the writer
    const writerErrorCallback = (error) => reject(error);
    // Create the zip writer
    zip.createWriter(blobWriter, writerIsSetUpCallback.bind(this), writerErrorCallback.bind(this));
  });

  return zipWriter;
}

/**
 * Retrieves photo data for all photos in the upload queue for a specific job.
 *
 * @param {string} jobId - The ID of the job to fetch queued photos for.
 * @returns {Promise<object[]>} A promise that resolves to an array of objects containing the photo key and its corresponding Blob data.
 */
async function getPhotosInUploadQueueForJob(jobId) {
  /** @type {QueueRecord[]} */
  const photosInQueue = await getQueueRecordsForJob(jobId);

  // Fetch the photo data for all queued photos
  const getQueuedPhotoDataPromises = photosInQueue.map(async (photoData) => {
    const { id: photoId } = photoData;
    const photoBlob = await getFile('photo_uploads', photoId);
    return { photoId, photoBlob, photoData };
  });

  // Wait for all photo data to be retrieved and return the results
  const queuedPhotos = await Promise.all(getQueuedPhotoDataPromises);
  return queuedPhotos;
}

/**
 * Adds photos to the zip writer.
 * @param {object[]} photos - The photos to add to the zip.
 * @param {object} zipWriter - The zip writer object.
 */
async function addPhotosToZip(photos, zipWriter) {
  // Set up an object to hold all the photo data for each photo
  const allPhotoDataById = {};

  // Add each queued photo to the zip
  for (const photo of photos) {
    const { photoId, photoBlob, photoData } = photo;
    if (!photoBlob) continue;

    // Add the photo data to the photo data object
    allPhotoDataById[photoId] = photoData;

    // Add the photo to the zip
    const addPhotoPromise = new Promise((resolve) => {
      const photoBlobReader = new zip.BlobReader(photoBlob);
      zipWriter.add(`${photoId}.jpg`, photoBlobReader, () => resolve());
    });
    await addPhotoPromise;
  }

  // Add the photo data as JSON file to the zip
  const addPhotoDataByIdPromise = new Promise((resolve) => {
    const photoDataByIdBlob = new Blob([JSON.stringify(allPhotoDataById)], { type: 'application/json' });
    const photoDataBlobReader = new zip.BlobReader(photoDataByIdBlob);
    zipWriter.add(`photo_data_by_id.json`, photoDataBlobReader, () => resolve());
  });
  await addPhotoDataByIdPromise;
}

/**
 * Adds a JSON file containing the changes to the zip writer.
 * @param {object} unsavedChanges - The unsaved changes to add to the zip as a JSON file.
 * @param {object} zipWriter - The zip writer object to add the JSON file to.
 */
async function addUnsavedChangesToZip(unsavedChanges, zipWriter) {
  if (!unsavedChanges) return;

  const addJsonFilePromise = new Promise((resolve) => {
    const jsonBlob = new Blob([JSON.stringify(unsavedChanges)], { type: 'application/json' });
    const jsonBlobReader = new zip.BlobReader(jsonBlob);
    zipWriter.add(`unsaved_changes.json`, jsonBlobReader, () => resolve());
  });
  await addJsonFilePromise;
}

/**
 * Gets the download URL for the finalized zip file from the zip writer.
 * @param {object} zipWriter - The zip writer object that has the data added to it and is ready to be finalized.
 * @returns {Promise<Blob>} The zip as a blob
 */
async function getZipAsBlob(zipWriter) {
  const resolveZipPromise = new Promise((resolve) => {
    // Finalize the zip and return the download url
    zipWriter.close((fullZipBlob) => resolve(fullZipBlob));
  });
  const zipAsBlob = await resolveZipPromise;
  return zipAsBlob;
}

/**
 * Clears all the queued photos for a specific job by removing their records from the upload queue store.
 * @param {string} jobId - The ID of the job for which to clear queued photos.
 */
async function clearQueuedPhotosForJob(jobId) {
  // Get all photos in the queue for the job
  /** @type {QueueRecord[]} */
  const photosInQueue = await getQueueRecordsForJob(jobId);

  // Remove each photo from the queue
  const removePhotoPromises = photosInQueue.map(async (photoData) => {
    const { id: photoKey } = photoData;
    // Remove the photo from the upload queue and notify progress listeners
    await removePhotoFromQueue(photoKey, { notifyRemoval: true });
  });
  await Promise.all(removePhotoPromises);
}
